import { useCallback, useEffect, useRef, useState } from 'react';
import { ErreurApi } from './api';

export interface EtatRequete<T> {
  donnees: T | null;
  erreur: ErreurApi | null;
  chargement: boolean;
  /** Relance l'appel en ignorant le cache. */
  rafraichir: () => void;
}

/**
 * Exécute un appel d'API et suit son état.
 *
 * `charger` reçoit le signal d'annulation et un booléen « ignorer le cache ».
 * L'annulation compte : le commercial passe d'une conversation à l'autre plus
 * vite que l'API ne répond, et une réponse tardive ne doit jamais écraser
 * l'affichage de la conversation suivante.
 *
 * `actif` à false laisse l'état vide sans déclencher d'appel — utile tant que
 * le contexte Front n'a pas encore livré l'identifiant attendu.
 */
export function useRequete<T>(
  charger: (signal: AbortSignal, forcer: boolean) => Promise<T>,
  dependances: ReadonlyArray<unknown>,
  actif = true,
): EtatRequete<T> {
  const [donnees, setDonnees] = useState<T | null>(null);
  const [erreur, setErreur] = useState<ErreurApi | null>(null);
  const [chargement, setChargement] = useState(actif);
  const [compteur, setCompteur] = useState(0);

  // Le contournement du cache ne vaut que pour l'appel déclenché par le clic
  // sur « Recharger ». Sans cette consommation, tout changement de conversation
  // postérieur au premier clic contournerait le cache à son tour.
  const forcerLaProchaine = useRef(false);

  const rafraichir = useCallback(() => {
    forcerLaProchaine.current = true;
    setCompteur((valeur) => valeur + 1);
  }, []);

  useEffect(() => {
    if (!actif) {
      setDonnees(null);
      setErreur(null);
      setChargement(false);
      return;
    }

    const forcer = forcerLaProchaine.current;
    forcerLaProchaine.current = false;

    const controleur = new AbortController();
    let abandonne = false;

    setChargement(true);
    setErreur(null);

    charger(controleur.signal, forcer)
      .then((resultat) => {
        if (abandonne) return;
        setDonnees(resultat);
        setChargement(false);
      })
      .catch((cause: unknown) => {
        if (abandonne || (cause as Error)?.name === 'AbortError') return;
        setDonnees(null);
        setErreur(
          cause instanceof ErreurApi
            ? cause
            : new ErreurApi(0, null, (cause as Error)?.message ?? 'Erreur inattendue'),
        );
        setChargement(false);
      });

    return () => {
      abandonne = true;
      controleur.abort();
    };
    // `charger` est recréée à chaque rendu : les dépendances réelles sont
    // celles que l'appelant déclare, plus le compteur de rafraîchissement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependances, actif, compteur]);

  return { donnees, erreur, chargement, rafraichir };
}
